// The parts of the client deliverable that are derived rather than written.
//
// Nobody types the executive summary or the next-steps list: both fall out of the ratings
// and weights the consultant already set. That is deliberate — a summary someone has to
// remember to update is a summary that goes stale, and the one thing a client deliverable
// cannot be is subtly out of date with the findings printed below it.
//
// Ported from the QBR reference (lib/assessment.ts) so the two products describe the same
// assessment the same way. The one vocabulary difference: its fifth rating is `gray`,
// ours is `unrated`.

const RATED = ["red", "yellow", "green"];

// Worst first, then heaviest first. The order a client meeting runs in.
const RATING_ORDER = { red: 0, yellow: 1, green: 2 };

const isRated = (item) => RATED.includes(item?.rating);
const weightOf = (item) => Number(item?.weight) || 0;

// "A", "A and B", "A, B, and C" — the Oxford comma is deliberate: these are read aloud.
const formatList = (names) => {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

// The executive summary: a headline for the score band, and a sentence naming what to do
// about it. Names the three heaviest red items, or the three heaviest yellow ones when
// nothing is red.
export function executiveSummary(items, score) {
  const assessed = (items || []).filter(isRated);

  if (assessed.length === 0) {
    return {
      headline: "This assessment is ready for discovery.",
      summary: "Score the assessment items and document findings to create a prioritized technology roadmap.",
    };
  }

  const n = Number(score) || 0;
  const headline =
    n >= 85 ? "Your technology posture is strong and well maintained."
    : n >= 70 ? "Your technology is on a solid foundation with focused improvements ahead."
    : n >= 50 ? "Your technology foundation has meaningful risks to address."
    : "Your technology posture requires a prioritized improvement plan.";

  const byWeight = (rating) => assessed
    .filter((i) => i.rating === rating)
    .sort((a, b) => weightOf(b) - weightOf(a))
    .slice(0, 3);

  const priorities = byWeight("red");
  const focus = priorities.length ? priorities : byWeight("yellow");

  const summary = focus.length
    ? `${priorities.length ? "Prioritize" : "Focus next on"} ${formatList(focus.map((i) => i.name))} to reduce risk and improve operational resilience.`
    : "Maintain the current standards, validate controls regularly, and continue documenting measurable business outcomes.";

  return { headline, summary };
}

// The next-steps list. An item only qualifies if someone actually wrote a recommendation
// for it — a numbered action with nothing to do under it is worse than a shorter list.
export function nextSteps(items, limit = 5) {
  const candidates = (items || [])
    .filter((i) => isRated(i) && (i.recommendation || "").trim())
    .sort((a, b) => (RATING_ORDER[a.rating] - RATING_ORDER[b.rating]) || (weightOf(b) - weightOf(a)));

  return { total: candidates.length, items: candidates.slice(0, Math.max(0, limit)) };
}

// Findings, in the order the consultant built them: categories by position, items by
// position within each. The old view flattened everything and re-sorted by rating, which
// silently threw that structure away.
export function findingsByCategory(categories, items) {
  return (categories || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((category) => ({
      category,
      items: (items || [])
        .filter((i) => i.category_id === category.id && isRated(i))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }))
    .filter((group) => group.items.length > 0);
}

// Every item in a category, rated or not — the glance grid shows coverage, so an unrated
// or out-of-scope item still belongs on it.
export function glanceByCategory(categories, items) {
  return (categories || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((category) => ({
      category,
      items: (items || [])
        .filter((i) => i.category_id === category.id)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }))
    .filter((group) => group.items.length > 0);
}

// The footer line. `report_tagline` is the tenant's own wording; {{organization}} is
// substituted so one stored string works for every client.
export function reportFooter(tagline, organization, tenantName) {
  // Tenant names routinely end in "LLC." or "Inc." — appending another full stop gives
  // "… by Business Technology Solutions Group LLC.." on every report.
  const name = (tenantName || "your technology partner").trim();
  const stop = /[.!?]$/.test(name) ? "" : ".";
  const fallback = `Technology guidance prepared exclusively for {{organization}} by ${name}${stop}`;
  return (tagline || fallback).replaceAll("{{organization}}", organization || "your organization");
}

export const contactLine = (tenant) =>
  [tenant?.website, tenant?.contact_email, tenant?.contact_phone]
    .map((v) => (v || "").trim())
    .filter(Boolean)
    .join(" · ");

// Up to two letters, the way the QBR reference draws a client with no logo.
export const initialsOf = (name) => (name || "")
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((w) => w[0])
  .join("")
  .toUpperCase() || "?";
